import { orderBy } from 'lodash';
import sha1 from 'js-sha1';
const apiFetchCachedResponses = window.apiFetchCachedResponses || {};
const ongoingRequests = new Map();
const maxSizeInBytes = 10 * 1024 * 1024; // 10 MB
const maxAgeInMilliseconds = 3600 * 1000; // 1 hour

const generateCacheKey = (options) => {
  const { url, path, body } = options;
  const { searchParams, href } = new URL(
    url || path,
    !url && path ? document.baseURI : undefined,
  );

  return sha1(
    JSON.stringify({
      href,
      body,
      searchParams: Object.fromEntries(searchParams),
    }),
  );
};

export const cacheMiddleware = (options, next) => {
  let responseOutput = '';
  // Check if caching is enabled for this request
  if (options.cachingEnabled) {
    const cacheKey = generateCacheKey(options);
    const now = Date.now();
    if (apiFetchCachedResponses.hasOwnProperty(cacheKey)) {
      const { timestamp, response } = apiFetchCachedResponses[cacheKey];
      const age = now - timestamp;
      if (age > maxAgeInMilliseconds) {
        // If the cached entry has exceeded the maximum age, remove it from the cache
        delete apiFetchCachedResponses[cacheKey];
        responseOutput = nextAndCache(options, next, cacheKey);
      } else {
        // Update last accessed time for LRU eviction
        apiFetchCachedResponses[cacheKey].lastAccessed = now;
        responseOutput = Promise.resolve(response);
      }
    } else if (ongoingRequests.has(cacheKey)) {
      // If a similar request is already in progress, wait for it to finish and resolve with its response
      responseOutput = ongoingRequests.get(cacheKey);
    } else {
      // Make the request and cache the response
      responseOutput = nextAndCache(options, next, cacheKey);
    }
  } else {
    // If caching is not enabled, proceed with the request without caching
    responseOutput = next(options);
  }
  return responseOutput;
};

const evictEntry = () => {
  const sortedEntries = orderBy(
    Object.entries(apiFetchCachedResponses),
    [(entry) => entry[1].lastAccessed, (entry) => -entry[1].sizeInBytes], // Sort by lastAccessed (ascending) and sizeInBytes (descending)
    ['asc', 'desc'],
  );

  if (sortedEntries.length > 0) {
    const [candidateCacheKey] = sortedEntries[0];
    delete apiFetchCachedResponses[candidateCacheKey];
  }
};

const currentCacheSizeInBytes = () =>
  Object.values(apiFetchCachedResponses).reduce(
    (acc, value) => acc + value.sizeInBytes,
    0,
  );

const sizeof = (object) => {
  const json = JSON.stringify(object);
  return json ? new Blob([json]).size : 0;
};

const nextAndCache = (options, next, cacheKey) => {
  // Make the request and cache the response
  const requestPromise = next(options)
    .then((response) => {
      // Evict oldest largest cache entries if cache size limit or max entries is reached
      while (currentCacheSizeInBytes() >= maxSizeInBytes) {
        evictEntry();
      }
      apiFetchCachedResponses[cacheKey] = {
        response,
        timestamp: Date.now(),
        lastAccessed: Date.now(),
        sizeInBytes: sizeof(response),
      };
      return response;
    })
    .catch((error) => {
      //eslint-disable-next-line
      console.error('Error fetching response:', error);
    });
  // Store the request promise in ongoing requests map
  ongoingRequests.set(cacheKey, requestPromise);
  requestPromise.finally(() => {
    ongoingRequests.delete(cacheKey);
  });
  return requestPromise;
};
